import type { ContainerKind, NodeKind } from "@/lib/arch-parser";

interface NodePalette {
  bg: string;
  border: string;
  text: string;
}

// Some ContainerKinds intentionally share a palette because they belong
// to the same visual family:
//   * service + api          → --node-service-*   (interface-layer services)
//   * queue + worker + job   → --node-worker-*    (background processing)
//   * database + storage     → --node-datastore-* (the "data lives here" anchor)
// If a future call splits any of these (e.g. api becomes its own visual
// kind), add a --node-<kind>-* token group in globals.css and update the
// matching entry in place.

const CONTAINER_PALETTE: Record<ContainerKind, NodePalette> = {
  frontend: {
    bg: "bg-[var(--node-frontend-fill)]",
    border: "border-[var(--node-frontend-border)]",
    text: "text-[var(--node-frontend-ink)]",
  },
  service: {
    bg: "bg-[var(--node-service-fill)]",
    border: "border-[var(--node-service-border)]",
    text: "text-[var(--node-service-ink)]",
  },
  api: {
    bg: "bg-[var(--node-service-fill)]",
    border: "border-[var(--node-service-border)]",
    text: "text-[var(--node-service-ink)]",
  },
  database: {
    bg: "bg-[var(--node-datastore-fill)]",
    border: "border-[var(--node-datastore-border)]",
    text: "text-[var(--node-datastore-ink)]",
  },
  storage: {
    bg: "bg-[var(--node-datastore-fill)]",
    border: "border-[var(--node-datastore-border)]",
    text: "text-[var(--node-datastore-ink)]",
  },
  cache: {
    bg: "bg-[var(--node-cache-fill)]",
    border: "border-[var(--node-cache-border)]",
    text: "text-[var(--node-cache-ink)]",
  },
  queue: {
    bg: "bg-[var(--node-worker-fill)]",
    border: "border-[var(--node-worker-border)]",
    text: "text-[var(--node-worker-ink)]",
  },
  worker: {
    bg: "bg-[var(--node-worker-fill)]",
    border: "border-[var(--node-worker-border)]",
    text: "text-[var(--node-worker-ink)]",
  },
  job: {
    bg: "bg-[var(--node-worker-fill)]",
    border: "border-[var(--node-worker-border)]",
    text: "text-[var(--node-worker-ink)]",
  },
  gateway: {
    bg: "bg-[var(--node-gateway-fill)]",
    border: "border-[var(--node-gateway-border)]",
    text: "text-[var(--node-gateway-ink)]",
  },
  "ml-model": {
    bg: "bg-[var(--node-ml-fill)]",
    border: "border-[var(--node-ml-border)]",
    text: "text-[var(--node-ml-ink)]",
  },
  other: {
    bg: "bg-[var(--node-other-fill)]",
    border: "border-[var(--node-other-border)]",
    text: "text-[var(--node-other-ink)]",
  },
};

const KIND_PALETTE: Record<Exclude<NodeKind, "container" | "component">, NodePalette> = {
  actor: {
    bg: "bg-[var(--node-actor-fill)]",
    border: "border-[var(--node-actor-border)]",
    text: "text-[var(--node-actor-ink)]",
  },
  "external-system": {
    bg: "bg-[var(--node-external-fill)]",
    border: "border-[var(--node-external-border)] border-dashed",
    text: "text-[var(--node-external-ink)]",
  },
  "system-boundary": {
    bg: "bg-background",
    border: "border-foreground/40 border-dashed",
    text: "text-foreground",
  },
  // v0.3 — placeholder palette for the System node introduced in Phase 5.
  // Same shape as system-boundary so the canvas doesn't crash if a v0.3
  // doc lands before Phase 5 styles it properly.
  system: {
    bg: "bg-background",
    border: "border-foreground/40 border-dashed",
    text: "text-foreground",
  },
};

export function containerPalette(kind: ContainerKind | undefined): NodePalette {
  if (!kind) return CONTAINER_PALETTE.other;
  return CONTAINER_PALETTE[kind] ?? CONTAINER_PALETTE.other;
}

export function kindPalette(kind: Exclude<NodeKind, "container" | "component">): NodePalette {
  return KIND_PALETTE[kind];
}
